import { accessoryCategories } from '@/content/accessories';
import { type IconKey } from '@/content/site';
import { type AccessoryCategory, type KnownAccessoryCategory } from '@/services/accessories/types';
import { type SectionPatternName } from '@/theme';

/**
 * The shelf's icon, label and plate texture, looked up once here rather than at
 * each call site.
 *
 * `content/accessories.ts` is a list because its order is the page's order, and
 * a list is the wrong shape for "what icon does this shelf use". These
 * functions are the index over it — and since 2026-10-26 the lookup can
 * genuinely MISS: shelves are rows an administrator may add from Kabex Hub
 * without a deploy, so a slug arriving here may have no artwork at all. Every
 * fallback below is therefore load-bearing rather than defensive, which is why
 * they live in one place instead of at seven call sites.
 */
const byValue = new Map(accessoryCategories.map((c) => [c.value as string, c]));

export const categoryIcon = (value: AccessoryCategory): IconKey =>
  byValue.get(value)?.icon ?? 'car';

/**
 * A shelf's name, for the ONE case where no row is at hand: a `?category=` slug
 * typed into the URL for a shelf that does not exist. Everywhere a part is
 * being rendered, use its own `categoryLabel` — the database is the only thing
 * that knows what a shelf an administrator added last week is called.
 */
export const categoryLabel = (value: AccessoryCategory): string =>
  byValue.get(value)?.label ?? value;

/**
 * Which texture each shelf's plate wears when no photograph is drawn.
 *
 * Not decoration for its own sake: a grid where every missing photograph is the
 * same rectangle reads as one broken image repeated twelve times, and a grid
 * where the texture changes with the shelf reads as a catalogue whose pictures
 * have not arrived. The patterns are the theme's own, so this cannot drift into
 * a second visual language.
 *
 * Here rather than in `AccessoryPhoto` because two places draw a plate for a
 * shelf — the catalogue card and the category tile — and a shelf that wore
 * `rings` in the grid and `columns` in the tile row would read as two different
 * shelves.
 */
const patterns: Record<KnownAccessoryCategory, SectionPatternName> = {
  'wheels-tyres': 'rings',
  exterior: 'columns',
  interior: 'aurora',
  electronics: 'rings',
  protection: 'columns',
  performance: 'rings',
  maintenance: 'columns',
};

/**
 * `rings` for an unknown shelf, not a fourth texture and not a blank: a new
 * shelf should look like the catalogue it joined, and `rings` is the one the
 * most shelves already wear.
 */
export const categoryPattern = (value: AccessoryCategory): SectionPatternName =>
  patterns[value as KnownAccessoryCategory] ?? 'rings';
